import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useClerk } from "@clerk/react";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Logo } from "@/components/layout/Logo";
import { AreaPicker } from "@/components/area/AreaPicker";
import { AreaSwitch } from "@/components/area/AreaSwitch";
import { AreaMobileNav, AreaSideNav } from "@/components/area/AreaNav";
import { ROLE_IT, setAreaPerms } from "@/components/crm/helpers";
import { AREA_TABS } from "@/components/area/areaTabs";
import { StaffGrantStatus, SupportPurpose } from "@/components/area/SupportPurpose";

const ALL = ["owner", "manager", "editor", "viewer", "support"];

function Header({ cur, tid, me }) {
  const nav = useNavigate();
  const { signOut } = useClerk();
  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-3 px-4 py-3 sm:px-8">
        <Link to="/area" className="flex items-center gap-2" data-testid="area-home"><Logo /><span className="hidden text-xs font-medium uppercase tracking-[0.2em] text-sky-700 sm:inline">Area clienti</span></Link>
        <div className="ml-auto flex min-w-0 items-center gap-2">
          {tid && cur && cur !== "purpose" && <AreaSwitch current={cur} onChange={(v) => nav(`/area/${v}`)} />}
          <span className="hidden max-w-[220px] truncate text-xs text-slate-500 md:inline" data-testid="area-user-email">{me?.email}</span>
          <Button size="sm" variant="ghost" onClick={() => signOut({ redirectUrl: "/sign-in" })} aria-label="Esci" title="Esci" data-testid="area-signout"><LogOut size={14} /></Button>
        </div>
      </div>
    </header>
  );
}

function useAreaTabs(t) {
  const api = useApi();
  const [mods, setMods] = useState(undefined);  // undefined = loading, false = unknown (show all), object = active modules
  useEffect(() => { setMods(undefined); api.get(`/t/${t.id}/crm/modules`).then(({ data }) => setMods(data)).catch(() => setMods(false)); }, [api, t.id]);
  if (mods === undefined) return null;
  return AREA_TABS.filter((x) => t.perms?.includes(x[5]) && (!x[4] || !mods || mods[x[4]]));
}

function Workspace({ t, onPermsChanged }) {
  const [tick, setTick] = useState(0);
  const [tab, setTab] = useState(() => { const q = new URLSearchParams(window.location.search); return q.get("tab") || (q.get("lp_state") ? "sources" : "overview"); });
  const [preset, setPreset] = useState(null);
  const role = t.status === "archived" ? "viewer" : t.role;
  setAreaPerms(t.status === "archived" ? t.perms.filter((p) => p.endsWith(".read")) : t.perms);
  const mods = useAreaTabs(t);
  const show = (k) => { setTab(k); const u = new URL(window.location.href); u.searchParams.set("tab", k); u.searchParams.delete("lp_state"); window.history.replaceState(window.history.state, "", u); };  // URL = visible section (reload / deep link)
  const go = (k, p = null) => { setPreset(p); show(k); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const change = (k) => { setPreset(null); show(k); };
  const active = !mods || !mods.length || mods.some(([k]) => k === tab) ? tab : (mods.find(([k]) => k === "overview") || mods[0])[0];  // ?tab= of a section not active here: back to Panoramica
  useEffect(() => { if (mods && active !== tab) show(active); }, [mods, active, tab]);  // eslint-disable-line react-hooks/exhaustive-deps -- normalizes the URL once modules are known
  return (
    <div className="space-y-5" data-testid="area-workspace">
      <div><h1 className="text-3xl font-bold tracking-tight text-navy-900 sm:text-4xl" data-testid="area-tenant-name">{t.name}</h1>
        <p className="mt-1 text-base text-slate-700" data-testid="area-role">Il tuo ruolo: {ROLE_IT[t.role] || "Assistenza MUSORA"}{t.status === "archived" ? " · cliente archiviato, sola lettura" : ""}</p>
        {t.role === "support" && <><SupportPurpose tid={t.id} exitTo="/area" /><StaffGrantStatus tid={t.id} /></>}</div>
      {!mods ? <p className="text-sm text-slate-600" data-testid="area-tabs-loading">Caricamento delle sezioni…</p> :
      <Tabs value={active} onValueChange={change} key={t.id} className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
        <AreaSideNav tabs={mods} value={active} onChange={change} />
        <AreaMobileNav tabs={mods} value={active} onChange={change} />
        <div className="min-w-0 flex-1">
        {mods.map(([k, , C]) => <TabsContent key={k} value={k} className="mt-0">
          <C key={k === "support" ? JSON.stringify(preset) : k} tid={t.id} role={role} tenant={t} tick={tick} go={go} preset={k === "support" ? preset : undefined}
            onChanged={() => setTick((x) => x + 1)} onPermsChanged={onPermsChanged} /></TabsContent>)}
        </div>
      </Tabs>}
    </div>
  );
}

export default function MemberArea() {
  const api = useApi();
  const { tid } = useParams();
  const [me, setMe] = useState(null);
  const [cur, setCur] = useState(undefined);  // undefined = loading, null = not accessible
  useEffect(() => { api.get("/me").then(({ data }) => setMe(data)).catch((e) => toast.error(errMsg(e))); }, [api]);
  const load = useCallback(() => {
    if (!tid) return Promise.resolve();
    return api.get(`/t/${tid}`).then(({ data }) => setCur(data.kind === "client" ? data : null)).catch((e) => {
      setCur(e?.response?.status === 428 ? "purpose" : null);  // 428: staff must first declare the purpose (PurposeGate)
      if (![403, 404, 428].includes(e?.response?.status)) toast.error(errMsg(e));
    });
  }, [api, tid]);
  useEffect(() => { setCur(undefined); load(); }, [load]);
  return (
    <div className="area-ui min-h-screen bg-background text-foreground" data-testid="member-area">
      <Header cur={cur} tid={tid} me={me} />
      <main className="mx-auto max-w-[1500px] px-4 py-6 sm:px-8 sm:py-10">
        {!tid ? (me ? <AreaPicker isAdmin={me.role === "admin"} /> : <p className="text-sm text-slate-500">Caricamento…</p>)
          : cur === undefined ? <p className="text-sm text-slate-500" data-testid="area-loading">Caricamento…</p>
          : cur === "purpose" ? <p className="text-sm text-slate-500" data-testid="area-purpose-needed">Scegli la finalità dell'accesso per aprire lo spazio.</p>
          : !cur ? <div className="mx-auto max-w-lg rounded-xl border border-red-200 bg-white p-8 text-center" data-testid="area-denied">
              <p className="text-base font-semibold text-slate-900">Accesso negato</p><p className="mt-2 text-sm text-slate-500">Non sei membro di questo spazio, oppure non esiste più.</p>
              <Button asChild variant="outline" className="mt-4"><Link to="/area">Torna ai tuoi spazi</Link></Button></div>
          : <Workspace t={cur} onPermsChanged={load} />}
      </main>
    </div>
  );
}
